import { useEffect, useState } from 'react';
import type { UserProfile } from '@penticton-dev-events/shared';

import { useFirebaseServices } from '../../../app/providers/FirebaseProvider';
import { getUserProfile } from '../../auth/api/userRepository';

export type OrganizerProfileState = {
  readonly profile: UserProfile | null;
  readonly loading: boolean;
  readonly error: Error | null;
};

export const useOrganizerProfile = (
  organizerUid: string | null,
): OrganizerProfileState => {
  const { firestore } = useFirebaseServices();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(organizerUid));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!organizerUid) {
      setProfile(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const fetchProfile = async (): Promise<void> => {
      try {
        const fetchedProfile = await getUserProfile(firestore, organizerUid);
        setProfile(fetchedProfile);
      } catch (fetchError) {
        setError(
          fetchError instanceof Error
            ? fetchError
            : new Error('Failed to fetch organizer profile'),
        );
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    void fetchProfile();
  }, [firestore, organizerUid]);

  return { profile, loading, error };
};

/**
 * Generates initials from a display name for avatar fallback
 */
export const getInitials = (displayName: string): string => {
  const trimmed = displayName.trim();
  if (!trimmed) return '??';

  const words = trimmed.split(/\s+/);
  if (words.length === 1) {
    return words[0]?.charAt(0)?.toUpperCase() || '';
  }

  return words
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('');
};
